import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MapPin, Search, Save } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import EventLocationPicker from '@/components/Map/EventLocationPicker';

interface Event {
  id: string;
  title: string;
  description?: string;
  event_date: string;
  end_date?: string;
  latitude: number;
  longitude: number;
  location_name: string;
  location_address?: string;
  event_type: 'public' | 'private';
  max_participants?: number;
  registration_deadline?: string;
  enable_chat: boolean;
  enable_checkin: boolean;
  checkin_radius?: number;
  status: 'active' | 'completed' | 'cancelled';
  user_id: string;
  created_at: string;
  cover_image_url?: string;
}

interface EditEventModalProps {
  event: Event | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEventUpdated: () => void;
}

export const EditEventModal = ({ event, open, onOpenChange, onEventUpdated }: EditEventModalProps) => {
  const [loading, setLoading] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [mapPosition, setMapPosition] = useState<{ lat: number; lng: number } | null>(null);
  const [address, setAddress] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_date: '',
    end_date: '',
    location_name: '',
    event_type: 'public' as 'public' | 'private',
    max_participants: '',
    registration_deadline: '',
    enable_chat: true,
    enable_checkin: false,
    checkin_radius: 100,
  });

  useEffect(() => {
    if (event && open) {
      setFormData({
        title: event.title,
        description: event.description || '',
        event_date: event.event_date ? new Date(event.event_date).toISOString().slice(0, 16) : '',
        end_date: event.end_date ? new Date(event.end_date).toISOString().slice(0, 16) : '',
        location_name: event.location_name,
        event_type: event.event_type,
        max_participants: event.max_participants?.toString() || '',
        registration_deadline: event.registration_deadline ? new Date(event.registration_deadline).toISOString().slice(0, 16) : '',
        enable_chat: event.enable_chat,
        enable_checkin: event.enable_checkin,
        checkin_radius: event.checkin_radius || 100,
      });
      setMapPosition({ lat: event.latitude, lng: event.longitude });
      setAddress(event.location_address || '');
      setTimeout(() => setShowMap(true), 100);
    }
  }, [event, open]);

  const searchAddress = async () => {
    if (!address.trim()) return;
    
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`
      );
      const data = await response.json();
      
      if (data && data.length > 0) {
        const result = data[0];
        setMapPosition({
          lat: parseFloat(result.lat),
          lng: parseFloat(result.lon)
        });
        setFormData(prev => ({
          ...prev,
          location_name: result.display_name
        }));
        toast.success('Indirizzo trovato!');
      } else {
        toast.error('Indirizzo non trovato, riprova');
      }
    } catch (error) {
      toast.error('Errore nella ricerca indirizzo');
    }
  };

  const handleMapClick = (lat: number, lng: number) => {
    setMapPosition({ lat, lng });
    setFormData(prev => ({
      ...prev,
      location_name: `Posizione aggiornata (${lat.toFixed(6)}, ${lng.toFixed(6)})`
    }));
    toast.success('Posizione aggiornata sulla mappa');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!event || !mapPosition) {
      toast.error('Seleziona una posizione sulla mappa');
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from('events')
        .update({
          ...formData,
          latitude: mapPosition.lat,
          longitude: mapPosition.lng,
          location_address: address || formData.location_name,
          max_participants: formData.max_participants ? parseInt(formData.max_participants) : null,
          updated_at: new Date().toISOString()
        })
        .eq('id', event.id);

      if (error) throw error;
      
      toast.success('Evento aggiornato con successo!');
      onOpenChange(false);
      onEventUpdated();
    } catch (error) {
      console.error('Error updating event:', error);
      toast.error('Errore nell\'aggiornamento evento');
    } finally {
      setLoading(false);
    }
  };

  if (!event) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Modifica Evento</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Titolo *</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Es. Passeggiata al parco con cani"
                  required
                />
              </div>
              
              <div>
                <label className="text-sm font-medium">Descrizione</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Descrivi il tuo evento..."
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Data e ora inizio *</label>
                  <Input
                    type="datetime-local"
                    value={formData.event_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, event_date: e.target.value }))}
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Fine (opzionale)</label>
                  <Input
                    type="datetime-local"
                    value={formData.end_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium">Tipo evento</label>
                <Select value={formData.event_type} onValueChange={(value: 'public' | 'private') => setFormData(prev => ({ ...prev, event_type: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="public">🌍 Pubblico</SelectItem>
                    <SelectItem value="private">🔒 Privato</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Max partecipanti</label>
                  <Input
                    type="number"
                    value={formData.max_participants}
                    onChange={(e) => setFormData(prev => ({ ...prev, max_participants: e.target.value }))}
                    placeholder="Illimitato"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Scadenza iscrizioni</label>
                  <Input
                    type="datetime-local"
                    value={formData.registration_deadline}
                    onChange={(e) => setFormData(prev => ({ ...prev, registration_deadline: e.target.value }))}
                  />
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Aggiorna posizione</label>
                <div className="flex gap-2 mt-2">
                  <Input
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="Cerca indirizzo..."
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), searchAddress())}
                  />
                  <Button type="button" onClick={searchAddress} variant="outline">
                    <Search className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              <div className="h-64 rounded-lg overflow-hidden border">
                {showMap ? (
                  <EventLocationPicker
                    position={mapPosition}
                    onChange={handleMapClick}
                    radius={formData.enable_checkin ? formData.checkin_radius : undefined}
                  />
                ) : (
                  <div className="h-full w-full bg-muted flex items-center justify-center">
                    <div className="text-center">
                      <MapPin className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground">Caricamento mappa...</p>
                    </div>
                  </div>
                )}
              </div>
              
              {mapPosition && (
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-sm font-medium">Posizione attuale:</p>
                  <p className="text-xs text-muted-foreground">
                    {formData.location_name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {mapPosition.lat.toFixed(6)}, {mapPosition.lng.toFixed(6)}
                  </p>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex gap-4 pt-4">
            <Button type="submit" disabled={loading || !mapPosition} className="flex-1">
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Salvando...' : 'Salva Modifiche'}
            </Button>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annulla
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};